// TempProviderDialog.js
// Create a temporary service provider from the Edit Job dialog: for times when
// no regular drivers/SPs are available and dispatch phones a local company.
// Only company name, contact name, phone, email, and zip are required.
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, TextField, Grid, Alert, CircularProgress, Typography, Box
} from '@mui/material';

const emptyForm = {
  companyName: '', contactName: '', phone: '', email: '', zip: '',
  address1: '', address2: '', city: '', state: ''
};

const TempProviderDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // { spUser, loginInfo, vendorNumber }

  const handleField = (name) => (e) => setForm(prev => ({ ...prev, [name]: e.target.value }));

  const handleClose = () => {
    if (submitting) return;
    setForm(emptyForm);
    setError(null);
    setCreated(null);
    onClose();
  };

  const handleCreate = async () => {
    const missing = [];
    if (!form.companyName.trim()) missing.push('Company Name');
    if (!form.contactName.trim()) missing.push('Contact Name');
    if (!form.phone.trim()) missing.push('Phone Number');
    if (!form.email.trim()) missing.push('Email Address');
    if (!form.zip.trim()) missing.push('Zip Code');
    if (missing.length > 0) {
      setError(`Required: ${missing.join(', ')}`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/facilities/temporary-provider`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create temporary provider.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseProvider = () => {
    if (created && onCreated) {
      onCreated(created.spUser);
    }
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Temporary Service Provider</DialogTitle>
      <DialogContent>
        {created ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>{created.message}</Alert>
            <Typography variant="subtitle2" gutterBottom>Login credentials (share with the provider):</Typography>
            <Typography variant="body2">Username: <strong>{created.loginInfo?.username}</strong></Typography>
            <Typography variant="body2">Password: <strong>{created.loginInfo?.password}</strong> (their Vendor ID)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              They will also receive these credentials by email when the job is dispatched to them.
              Remember: the agreed amount must be on the job invoice before you can dispatch.
            </Typography>
          </Box>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              For local providers helping with a single job. Creates a Vendor ID, a temporary
              facility, and a dashboard-only login (username = email, password = Vendor ID).
            </DialogContentText>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField fullWidth required label="Company Name" value={form.companyName} onChange={handleField('companyName')} margin="dense" size="small" autoFocus/>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Contact Name" value={form.contactName} onChange={handleField('contactName')} margin="dense" size="small"/>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Phone Number" value={form.phone} onChange={handleField('phone')} margin="dense" size="small"/>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth required label="Email Address" type="email" value={form.email} onChange={handleField('email')} margin="dense" size="small"/>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth required label="Zip Code" value={form.zip} onChange={handleField('zip')} margin="dense" size="small"/>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Street Address (optional)" value={form.address1} onChange={handleField('address1')} margin="dense" size="small"/>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City (optional)" value={form.city} onChange={handleField('city')} margin="dense" size="small"/>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="State (optional)" value={form.state} onChange={handleField('state')} margin="dense" size="small" inputProps={{ maxLength: 2 }}/>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          <>
            <Button onClick={handleClose}>Close</Button>
            <Button variant="contained" color="primary" onClick={handleUseProvider}>
              Assign This Provider
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleCreate} disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : null}>
              {submitting ? 'Creating...' : 'Create Provider'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TempProviderDialog;
